import { Badge, useMantineTheme } from '@mantine/core';
import {
  IconBorderAll,
  IconCircles,
  IconCreditCard,
  IconFileDescription,
  IconFolder,
  IconGift,
  IconKey,
  IconLink,
  IconMail,
  IconRotateRectangle,
  IconShieldHalfFilled,
  IconUser,
  IconUsers,
} from '@tabler/icons';
import { FC, useContext } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import NavigationLink from '../../Atoms/Navigation/NavigationLink';
import { useNavigationItem } from '../../Organismns/Navigation/NavigationItemHook';
import { WindowContext } from '../../Service/Context/WindowContext';
import { TestS } from '../../TestS';
import { ConstantS } from '../../Service/ConstantS';
import { MixpanelS } from '../../Service/MixpanelS';
import { ProfileContext } from '../../Service/Context/ProfileContext';
import { LicenceS } from '../../Service/LicenceS';
import { ThemeTitle } from '../../Atoms/ThemeText';
import { useBookitupScope } from '../../Service/Context/BookitupSubscriptionContext';

const SettingsNavbar: FC<{ closeNavBar: () => void }> = ({ closeNavBar }) => {
  const { colorScheme } = useMantineTheme();
  const { sm } = useContext(WindowContext);
  const navigate: NavigateFunction = useNavigate();
  const { profile } = useContext<any>(ProfileContext);
  const { isOwner, isScopeSelected: hasSettingsScope } = useBookitupScope('SETTINGS');
  const { isScopeSelected: hasResourceScope } = useBookitupScope('RESOURCE');
  const { isScopeSelected: hasDocumentsScope } = useBookitupScope('DOCUMENTS');
  const userDataItemActive = useNavigationItem('/settings/userdata');
  const bookitupTeamItemActive = useNavigationItem('/settings/bookitup-team');
  const recruitmentItemActive = useNavigationItem('/settings/recruitment');
  const plansItemActive = useNavigationItem('/settings/plans');
  const invoicesItemActive = useNavigationItem('/settings/invoices');
  const customFieldsItemActive = useNavigationItem('/settings/customfields');
  const eventStatesItemActive = useNavigationItem('/settings/eventstates');
  const eventKindsItemActive = useNavigationItem('/settings/eventkinds');
  const googleItemActive = useNavigationItem('/settings/google');
  const calendarItemActive = useNavigationItem('/settings/calendar');
  const contactFormItemActive = useNavigationItem('/settings/contact-form');
  const bookkeepingItemActive = useNavigationItem('/settings/bookkeeping');
  const teamItemActive = useNavigationItem('/settings/team');
  const resourceItemActive = useNavigationItem('/settings/resources');
  const workflowItemActive = useNavigationItem('/settings/workflow');
  const messagesItemActive = useNavigationItem('/settings/messages');
  const agencyMessagesItemActive = useNavigationItem('/settings/agency/messages');
  const mailAccountItemActive = useNavigationItem('/settings/mail-account');
  const signatureItemActive = useNavigationItem('/settings/signature');
  const templatesItemActive = useNavigationItem('/settings/templates');
  const positionsItemActive = useNavigationItem('/settings/positions');
  const questionnairesItemActive = useNavigationItem('/settings/questionnaires');
  const contractsItemActive = useNavigationItem('/settings/contracts');
  const documentsItemActive = useNavigationItem('/settings/documents');
  const taxesItemActive = useNavigationItem('/settings/taxes');
  const sequentialNumbersItemActive = useNavigationItem('/settings/sequentialnumbers');
  const filesItemActive = useNavigationItem('/settings/files');
  const gdprItemActive = useNavigationItem('/settings/gdpr');
  const accountItemActive = useNavigationItem('/settings/account');

  // eslint-disable-next-line no-underscore-dangle
  const _onClick = (path: string, section: string) => {
    navigate(path);
    closeNavBar();
    MixpanelS.track(ConstantS.TrackingEvents.ViewContent, { page: 'Settings', section });
  };

  const signatureNavigationLink = (
    <NavigationLink
      label="Signatur"
      active={signatureItemActive}
      onClick={() => _onClick('/settings/signature', 'Signature')}
      variant="filled"
    />
  );

  return (
    <>
      {!sm && (
        <ThemeTitle p="md" order={3} mb={5}>
          Einstellungen
        </ThemeTitle>
      )}
      {hasSettingsScope && (
        <>
          <NavigationLink
            variant="filled"
            label="Profil"
            onClick={() => _onClick('/settings/userdata', 'Profile')}
            active={userDataItemActive}
            icon={sm && sm && <IconUser size={16} />}
            testId={TestS.Navigate.SETTINGS_TO_USER_DATA}
          />
          {isOwner && (
            <NavigationLink
              label="Team"
              icon={sm && <IconUsers size={16} />}
              active={bookitupTeamItemActive}
              onClick={() => _onClick('/settings/bookitup-team', 'Team')}
              variant="filled"
              rightSection={
                <Badge
                  size="md"
                  radius="sm"
                  color="orange"
                  variant={colorScheme === 'dark' ? 'light' : 'filled'}
                  sx={{ textTransform: 'none' }}
                >
                  Neu
                </Badge>
              }
            />
          )}
          <NavigationLink
            variant="filled"
            label="Freunde werben"
            onClick={() => _onClick('/settings/recruitment', 'Recruitment')}
            active={recruitmentItemActive}
            icon={sm && <IconGift size={16} />}
          />
          {isOwner && (
            <NavigationLink
              label="Paket &amp; Abrechnung"
              icon={sm && <IconCreditCard size={16} />}
              childrenOffset={28}
            >
              <NavigationLink
                label="Paket"
                active={plansItemActive}
                onClick={() => _onClick('/settings/plans', 'Billing plans')}
                variant="filled"
              />
              <NavigationLink
                label="Abrechnung"
                active={invoicesItemActive}
                onClick={() => _onClick('/settings/invoices', 'Abrechnung')}
                variant="filled"
              />
            </NavigationLink>
          )}

          <NavigationLink
            label="Eigene Felder, Status & Art"
            icon={sm && <IconBorderAll size={16} />}
            childrenOffset={28}
            variant="filled"
          >
            <NavigationLink
              label="Eigene Felder"
              active={customFieldsItemActive}
              onClick={() => _onClick('/settings/customfields', 'Custom fields')}
              variant="filled"
            />
            <NavigationLink
              label="Eigene Status"
              active={eventStatesItemActive}
              onClick={() => _onClick('/settings/eventstates', 'Event states')}
              variant="filled"
            />
            <NavigationLink
              label="Eigene Auftragsarten"
              active={eventKindsItemActive}
              onClick={() => _onClick('/settings/eventkinds', 'Event kinds')}
              variant="filled"
            />
          </NavigationLink>

          <NavigationLink
            label="Integrationen"
            icon={sm && <IconLink size={16} />}
            childrenOffset={28}
            variant="filled"
          >
            <NavigationLink
              label="Google-Sync"
              active={googleItemActive}
              onClick={() => _onClick('/settings/google', 'Google panel')}
              variant="filled"
            />
            <NavigationLink
              label="Kalender-Sync"
              active={calendarItemActive}
              onClick={() => _onClick('/settings/calendar', 'External calendar')}
              variant="filled"
            />
            {profile && profile.kindOfWebsite && (
              <NavigationLink
                label="Webseite verbinden"
                active={location.pathname.includes('connectwebsite')}
                onClick={() => _onClick('/settings/connectwebsite', 'Connect website')}
                variant="filled"
              />
            )}
            <NavigationLink
              label="Kontaktformulare"
              active={contactFormItemActive}
              onClick={() => _onClick('/settings/contact-form', 'Contact form')}
              variant="filled"
            />
            <NavigationLink
              label="Buchhaltung"
              active={bookkeepingItemActive}
              onClick={() => _onClick('/settings/bookkeeping', 'Bookkeeping')}
              variant="filled"
            />
          </NavigationLink>

          <NavigationLink
            label="Dienstleister-Netzwerk"
            icon={sm && <IconUsers size={16} />}
            active={teamItemActive}
            onClick={() => _onClick('/settings/team', 'Team')}
            variant="filled"
          />
          {profile && hasResourceScope && LicenceS.hasAccessToResourceModule(profile.id) && (
            <NavigationLink
              label="Ressourcen"
              icon={sm && <IconCircles size={16} />}
              active={resourceItemActive}
              onClick={() => _onClick('/settings/resources', 'Resources')}
              variant="filled"
            />
          )}
          <NavigationLink
            label="Workflow"
            icon={sm && <IconRotateRectangle size={16} />}
            active={workflowItemActive}
            onClick={() => _onClick('/settings/workflow', 'Workflow')}
            variant="filled"
          />

          <NavigationLink label="E-Mail" icon={sm && <IconMail size={16} />} childrenOffset={28} variant="filled">
            <NavigationLink
              label="Vorlagen"
              active={messagesItemActive}
              onClick={() => _onClick('/settings/messages', 'Messages')}
              variant="filled"
            />

            <NavigationLink
              label="Agenturvorlagen"
              active={agencyMessagesItemActive}
              onClick={() => _onClick('/settings/agency/messages', 'Agency-Messages')}
              variant="filled"
            />

            <NavigationLink
              label="Ausgangsserver"
              active={mailAccountItemActive}
              onClick={() => _onClick('/settings/mail-account', 'Mail-account')}
              variant="filled"
            />
            {signatureNavigationLink}
          </NavigationLink>

          {hasDocumentsScope && (
            <NavigationLink
              label="Dokumente"
              icon={sm && <IconFileDescription size={16} />}
              childrenOffset={28}
              variant="filled"
              testId={TestS.Navigate.SETTINGS_TO_DOCUMENTS}
            >
              <NavigationLink
                label="Dokument-Vorlagen"
                active={templatesItemActive}
                onClick={() => _onClick('/settings/templates', 'Document templates')}
                variant="filled"
              />
              <NavigationLink
                label="Produkte"
                active={positionsItemActive}
                onClick={() => _onClick('/settings/positions', 'Products')}
                variant="filled"
              />
              <NavigationLink
                label="Fragebogen-Vorlagen"
                active={questionnairesItemActive}
                onClick={() => _onClick('/settings/questionnaires', 'Questionnaires templates')}
                variant="filled"
              />
              <NavigationLink
                label="Vertrags-Vorlagen"
                active={contractsItemActive}
                onClick={() => _onClick('/settings/contracts', 'Contracts templates')}
                variant="filled"
              />
              <NavigationLink
                label="Dokument-Layout"
                active={documentsItemActive}
                onClick={() => _onClick('/settings/documents', 'Dokument Layout')}
                variant="filled"
                testId={TestS.Navigate.SETTINGS_TO_DOCUMENT_LAYOUT}
              />
              <NavigationLink
                label="Mehrwertsteuer"
                active={taxesItemActive}
                onClick={() => _onClick('/settings/taxes', 'Taxes')}
                variant="filled"
                testId={TestS.Navigate.SETTINGS_TO_TAXES}
              />
              <NavigationLink
                label="Nummernkreise"
                active={sequentialNumbersItemActive}
                onClick={() => _onClick('/settings/sequentialnumbers', 'Sequential numbers')}
                variant="filled"
              />
            </NavigationLink>
          )}

          <NavigationLink
            label="Dateien"
            icon={sm && <IconFolder size={16} />}
            active={filesItemActive}
            onClick={() => _onClick('/settings/files', 'Files')}
            variant="filled"
          />
          <NavigationLink
            label="Datenschutz"
            icon={sm && <IconShieldHalfFilled size={16} />}
            active={gdprItemActive}
            onClick={() => _onClick('/settings/gdpr', 'GDPR')}
            variant="filled"
          />
        </>
      )}
      {!hasSettingsScope && (
        <>
          <NavigationLink
            label="Kalender-Sync"
            active={calendarItemActive}
            onClick={() => _onClick('/settings/calendar', 'External calendar')}
            variant="filled"
          />
          <NavigationLink
            label="E-Mail"
            icon={sm && <IconMail size={16} />}
            childrenOffset={28}
            defaultOpened={signatureItemActive}
            variant="filled"
          >
            {signatureNavigationLink}
          </NavigationLink>
        </>
      )}
      <NavigationLink
        label="Passwort &amp; Account"
        icon={sm && <IconKey size={16} />}
        active={accountItemActive}
        onClick={() => _onClick('/settings/account', 'Account details')}
        variant="filled"
      />
    </>
  );
};

export default SettingsNavbar;
